import React, { useEffect, useState } from "react";
import { Snackbar } from "@mui/material";
import { Routes, Route, Navigate } from "react-router-dom";
import { Provider, useSelector } from "react-redux";
import { PersistGate } from "redux-persist/integration/react";
import { store, persistor, RootState } from "./redux/store";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import enGB from "date-fns/locale/en-GB";
import "./App.css";
import Welcome from "./views/Welcome";
import Home from "./views/Home";
import AuthAction from "./views/AuthAction";
import SubscriptionSuccess from "./views/SubscriptionSuccess";

function App() {
  const [networkStatus, setNetworkStatus] = useState({
    online: navigator.onLine,
    message: navigator.onLine
      ? "Back online"
      : "You are offline - check your internet connection",
  });
  const [openSnackbar, setOpenSnackbar] = useState(false);

  useEffect(() => {
    const handleOnline = () => {
      setNetworkStatus({ online: true, message: "Back online" });
      setOpenSnackbar(true);
      setTimeout(() => setOpenSnackbar(false), 2000); // Hide after 2 seconds
    };

    const handleOffline = () => {
      setNetworkStatus({
        online: false,
        message: "You are offline - check your internet connection",
      });
      setOpenSnackbar(true);
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    // Cleanup function to remove the event listeners
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  const handleCloseSnackbar = (
    event: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === "clickaway") {
      return;
    }
    setOpenSnackbar(false);
  };

  return (
    <>
      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        message={networkStatus.message}
        ContentProps={{
          sx: {
            backgroundColor: networkStatus.online ? "green" : "red",
            justifyContent: "center",
          },
        }}
      />
      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
        <Provider store={store}>
          <PersistGate loading={null} persistor={persistor}>
            <AppRoutes />
          </PersistGate>
        </Provider>
      </LocalizationProvider>
    </>
  );
}

// Separate component to use Redux hooks
function AppRoutes() {
  // Get user state from Redux store
  // Note: Adjust the state path according to your state structure
  const user = useSelector((state: RootState) => state.user);

  return (
    <Routes>
      <Route
        path="/"
        element={user && user.uid ? <Navigate to="/home" /> : <Welcome />}
      />
      <Route path="/welcome" element={<Welcome />} />
      <Route path="/authAction" element={<AuthAction />} />
      <Route path="/subscription-success" element={<SubscriptionSuccess />} />
      <Route path="/home" element={<Home />} />
    </Routes>
  );
}

export default App;
